<template>
  <div v-if="state === DateTimeFilterTypes.Live">
    <button type="button" class="btn slider-button NKT-P2">
      <img src="@/assets/svg/time-white.svg" class="icon" />
      {{ reactiveTime }}
      <div class="spinner-border spinner-border-sm loading-indicator" v-if="isLoading">
        <span class="visually-hidden">Loading...</span>
      </div>
    </button>
  </div>
  <div v-else class="">
    <div class="d-flex flex-row align-items-center justify-content-between">
      <div class="NKT-P2 slider-text" id="min">
        <span>00:00</span>
      </div>
      <div class="flex-fill position-relative mx-3 toggle-click-child">
        <div>
          <input
            type="range"
            class="form-range slider"
            min="0"
            max="1439"
            step="10"
            id="timeRange"
            v-model="timeRange"
            @input="updateSliderTime"
          />
        </div>
        <button
          type="button"
          id="slider-button"
          class="btn slider-button NKT-P2 mt-1 position-absolute"
          :style="{
            left: moveButton(),
            transform: 'translateX(-50%)'
          }"
        >
          <img src="@/assets/svg/time-white.svg" class="icon" />
          {{ reactiveTime }}
          <div class="spinner-border spinner-border-sm loading-indicator" v-if="isLoading">
            <span class="visually-hidden">Loading...</span>
          </div>
        </button>
      </div>
      <div class="NKT-P2 slider-text" id="max">
        <span>24:00</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, defineProps, onUpdated, computed, watch, PropType } from 'vue'
import { useDateTimeStore } from '@/stores/DateTimeFilterStore'
import { DateTimeFilterTypes } from '@/utilities/enums'

const props = defineProps({
  state: String as PropType<DateTimeFilterTypes>,
  isLoading: Boolean
})
const timeRange = ref(0)
const timeRangePrev = ref(0)

const buttonWidth = ref(0)

const dateTimeStore = useDateTimeStore()

const updateSliderTime = () => {
  const hoursValue = timeRange.value / 60
  const minutesValue = timeRange.value % 60

  const hours = Math.floor(hoursValue).toString().padStart(2, '0')

  const minutes = minutesValue.toString().padStart(2, '0')

  if (isFutureDate(hours, minutes)) {
    timeRange.value = timeRangePrev.value
    return
  }

  const time = `${hours}:${minutes}`
  dateTimeStore.setTime(time)
}

const isFutureDate = (hours: string, minutes: string) => {
  const dateNow = new Date()
  const dateStored = dateTimeStore.dateTime!

  const isSameDate =
    dateNow.getDate() === dateStored.getDate() &&
    dateNow.getMonth() === dateStored.getMonth() &&
    dateNow.getFullYear() === dateStored.getFullYear()

  if (isSameDate) {
    const storedHours = dateNow.getHours()
    const storedMinutes = dateNow.getMinutes()
    const inputHours = parseInt(hours, 10)
    const inputMinutes = parseInt(minutes, 10)

    if (storedHours < inputHours || (storedHours === inputHours && storedMinutes < inputMinutes)) {
      return true
    }

    return false
  }

  return false
}

const moveButton = () => {
  return `${(timeRange.value / 1439) * 100}%`
}

onUpdated(() => {
  const button = document.getElementById('slider-button')
  if (button) {
    buttonWidth.value = button.offsetWidth
  }
})

const reactiveTime = computed(() => dateTimeStore.time)

watch(
  () => [props.state, dateTimeStore.time],
  ([newVal]) => {
    if (newVal !== DateTimeFilterTypes.Live) {
      const [hours, minutes] = reactiveTime.value.split(':').map(Number)
      timeRange.value = hours * 60 + minutes
    }
  }
)

watch(
  () => timeRange.value,
  (_, oldVal) => {
    timeRangePrev.value = oldVal
  }
)
</script>

<style scoped lang="scss">
@use '@/styles/ms-color-styles.scss' as color;

.loading-indicator {
  height: 12px;
  width: 12px;
}

.slider {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 4px;
  background: color.$color-nordic-blue-base;
  border-radius: 3px;
  outline: none;
  opacity: 0.9;
  transition: opacity 0.2s;

  // Track fill (progress)
  &::-webkit-slider-runnable-track {
    background: color.$color-nordic-blue-base;
    height: 4px;
    border-radius: 3px;
  }
  &::-moz-range-track {
    background: color.$color-nordic-blue-base;
    height: 4px;
    border-radius: 3px;
  }

  // Slider handle
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 14px;
    height: 14px;
    background: var(--offshore-violet-offshore-violet-tint-50, #ab98fa);
    border-radius: 50%;
    cursor: pointer;
    transition: background 0.2s;
  }
  &::-moz-range-thumb {
    width: 14px;
    height: 14px;
    background: var(--offshore-violet-offshore-violet-tint-50, #ab98fa);
    border-radius: 50%;
    cursor: pointer;
  }
}

.slider-button {
  border-radius: var(--border-radius-small-tryout, 4px);
  background: color.$color-offshore-violet !important;
  color: color.$color-white;
  padding: var(--border-radius-small-tryout, 4px) 6px;
  display: flex;
  align-items: center;
  gap: var(--border-radius-small-tryout, 4px);
  pointer-events: none;
  flex-shrink: 0;
  z-index: 10;
}

.toggle-click-child {
  pointer-events: auto;
}

.slider-text {
  color: color.$color-dark-grey;
}
</style>
